/**
 * Module 10 — Settings · shared view helpers
 *
 * Small atoms reused across the seven Settings surfaces: the org-id/role hooks
 * (from the global scope store), the uppercase `.section-label`, and a relative
 * "2m ago" formatter for last-active timestamps.
 */
'use client';

import * as React from 'react';
import { Search, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useScopeStore } from '@/lib/stores/scope-store';
import { useRole as useCanonicalRole } from '@/lib/hooks/useRole';
import type { Role } from '@/lib/api/services/session';

export function useOrgId(): string | undefined {
  return useScopeStore((s) => s.org?.id) ?? undefined;
}

/** Settings-flavoured accessor: the bare role, since every settings view compares
 *  it directly. Delegates to the canonical hook so there is one implementation
 *  rather than two same-named hooks reading the store independently. */
export function useRole(): Role | null {
  return useCanonicalRole().role;
}

/** The signed-in user's id — used to disable self-administration in the UI
 *  (UM-F3; the server is the authority and also rejects it). */
export function useUserId(): string | undefined {
  return useScopeStore((s) => s.user?.id) ?? undefined;
}

/** The 10 Fabric modules (nav.* capability groups) grantable in the access
 *  envelope, with display labels. Restrict-only + bounded by the tier above
 *  (org ⊇ dept ⊇ project); the server enforces the ceiling. */
export const MODULE_OPTIONS = [
  'nav.workspace', 'nav.connect', 'nav.orchestrate', 'nav.context-graph', 'nav.actions',
  'nav.simulate', 'nav.data', 'nav.governance', 'nav.insights', 'nav.settings',
];
export const MODULE_LABELS: Record<string, string> = {
  'nav.workspace': 'Workspace', 'nav.connect': 'Connect', 'nav.orchestrate': 'Orchestrate',
  'nav.context-graph': 'Context Graph', 'nav.actions': 'Actions', 'nav.simulate': 'Simulate',
  'nav.data': 'Data', 'nav.governance': 'Governance', 'nav.insights': 'Insights', 'nav.settings': 'Settings',
};

/** RBAC-by-hiding helpers (Blueprint §4.2). A null role (not yet hydrated) is
 *  treated permissively so the surface still renders for inspection. */
export const rbac = {
  canEditOrgProfile: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin',
  canEditDeployment: (r: Role | null) => !r || r === 'super_admin',
  canManageUsers: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin' || r === 'dept_admin',
  // Managing a department = renaming it, tightening its scopes, administering
  // its people. A dept_admin may do all of that to the department they were
  // given (the server bounds them to it via administersDepartment).
  canManageDepartments: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin' || r === 'dept_admin',
  // CREATING one is a different question, and org-wide only: a dept_admin
  // administers the department they were given rather than minting new ones.
  // Kept separate from canManageDepartments because conflating the two is what
  // put a "New department" button in the dept_admin's scope switcher, pointing
  // at an endpoint that now answers 403 (identity
  // services/authz/creation-authority.js is the authority).
  canCreateDepartment: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin',
  // Scope grants (connectors/models/entity-types) are super-admin controlled —
  // they govern everything a department's projects may use. org_admin may also
  // grant; dept_admin can only tighten (restrict-only) within the granted set.
  canGrantScopes: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin',
  canEditPolicy: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin',
  // The AI Constitution is org-scoped, so the org owner/admin (identity 'OWNER'
  // → org_admin) manages it, mirroring canEditPolicy. super_admin is the
  // cross-org platform admin; org_admin is the org's own governance owner.
  canAmendConstitution: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin',
  canViewBilling: (r: Role | null) => !r || r === 'super_admin',
  // Who may create a project. Deliberately excludes project_admin (they
  // administer the project they were given, they don't spawn new ones) and
  // plain members. This gates the empty-state CTA an invitee lands on, so a
  // member who was invited into an org with no visible projects is told to ask
  // an admin rather than being handed a button the API would reject.
  canCreateProject: (r: Role | null) => !r || r === 'super_admin' || r === 'org_admin' || r === 'dept_admin',
  // NOTE: HITL "can review approvals" is intentionally NOT here — it depends on
  // project/department ownership, not just the module role, so it's computed by
  // the backend and delivered as the `hitl.review` capability (see
  // services/authz/delegation.canReviewApprovals + the session bootstrap).
};

export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('text-[11px] font-semibold uppercase tracking-wider text-cs-text-muted', className)}>
      {children}
    </p>
  );
}

export function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Searchable checkbox multi-select over a catalog of string options.
 *  Shared by the Departments scopes drawer and the Department console. */
export function CheckboxList({ label, options, selected, onChange, loading, disabled, testid, labels }: {
  label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void;
  loading?: boolean; disabled?: boolean; testid: string;
  /** Optional value→display-label map. When the option strings are opaque ids
   *  (e.g. project ObjectIds), supply human labels for display + search. */
  labels?: Record<string, string>;
}) {
  const [q, setQ] = React.useState('');
  const sel = new Set(selected);
  const labelOf = (o: string) => labels?.[o] ?? o;
  // Show selected-but-not-in-catalog items too, so nothing silently disappears.
  const all = Array.from(new Set([...options, ...selected]));
  const filtered = q.trim() ? all.filter((o) => labelOf(o).toLowerCase().includes(q.trim().toLowerCase())) : all;
  const toggle = (o: string) => {
    if (disabled) return;
    onChange(sel.has(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  };

  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-[11px] font-semibold text-cs-text-muted">{label}</label>
        <span className="text-[11px] text-cs-text-muted">{selected.length} selected</span>
      </div>
      <div className="relative mb-1.5">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-cs-text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="h-8 pl-8 text-xs" />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-lg border border-cs-border bg-cs-surface-raised p-1">
        {loading ? (
          <p className="px-2 py-3 text-xs text-cs-text-muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-cs-text-muted">{all.length === 0 ? `No ${label.toLowerCase()} available.` : 'No matches.'}</p>
        ) : (
          filtered.map((o) => {
            const on = sel.has(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)} disabled={disabled}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                  on ? 'text-cs-primary-400' : 'text-cs-text-secondary hover:bg-cs-surface-hover',
                  disabled && 'cursor-not-allowed opacity-60',
                )}>
                <span className={cn(
                  'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                  on ? 'border-cs-primary-500 bg-cs-primary-500' : 'border-cs-border',
                )}>
                  {on && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="truncate">{labelOf(o)}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/** Searchable single-select (radio) over a catalog of string options. Used by
 *  the Department Scopes tab to pick the ONE project being configured. Passing
 *  the already-selected value again clears the selection. */
export function RadioList({ label, options, selected, onChange, loading, disabled, testid, labels }: {
  label: string; options: string[]; selected: string | null; onChange: (v: string | null) => void;
  loading?: boolean; disabled?: boolean; testid: string;
  /** Optional value→display-label map for opaque ids (e.g. project ObjectIds). */
  labels?: Record<string, string>;
}) {
  const [q, setQ] = React.useState('');
  const labelOf = (o: string) => labels?.[o] ?? o;
  // Show the selected-but-not-in-catalog value too, so nothing silently vanishes.
  const all = Array.from(new Set([...options, ...(selected ? [selected] : [])]));
  const filtered = q.trim() ? all.filter((o) => labelOf(o).toLowerCase().includes(q.trim().toLowerCase())) : all;
  const pick = (o: string) => {
    if (disabled) return;
    onChange(selected === o ? null : o);
  };

  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-[11px] font-semibold text-cs-text-muted">{label}</label>
        <span className="text-[11px] text-cs-text-muted">{selected ? '1 selected' : 'none'}</span>
      </div>
      <div className="relative mb-1.5">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-cs-text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="h-8 pl-8 text-xs" />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-lg border border-cs-border bg-cs-surface-raised p-1">
        {loading ? (
          <p className="px-2 py-3 text-xs text-cs-text-muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-cs-text-muted">{all.length === 0 ? `No ${label.toLowerCase()} available.` : 'No matches.'}</p>
        ) : (
          filtered.map((o) => {
            const on = selected === o;
            return (
              <button key={o} type="button" onClick={() => pick(o)} disabled={disabled}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                  on ? 'text-cs-primary-400' : 'text-cs-text-secondary hover:bg-cs-surface-hover',
                  disabled && 'cursor-not-allowed opacity-60',
                )}>
                <span className={cn(
                  'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
                  on ? 'border-cs-primary-500' : 'border-cs-border',
                )}>
                  {on && <span className="h-2 w-2 rounded-full bg-cs-primary-500" />}
                </span>
                <span className="truncate">{labelOf(o)}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/** Chip/tag input — type + Enter to add (used for entity types until a catalog API exists). */
export function TagInput({ label, value, onChange, disabled, testid, placeholder }: {
  label: string; value: string[]; onChange: (v: string[]) => void; disabled?: boolean; testid: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = React.useState('');
  const add = () => {
    const t = draft.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft('');
  };
  return (
    <div data-testid={testid}>
      <label className="mb-1.5 block text-[11px] font-semibold text-cs-text-muted">{label}</label>
      {value.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-full border border-cs-border bg-cs-surface-hover px-2 py-0.5 text-[11px] text-cs-text-secondary">
              {t}
              {!disabled && <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="hover:text-cs-error-400"><X className="h-2.5 w-2.5" /></button>}
            </span>
          ))}
        </div>
      )}
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} disabled={disabled}
        placeholder={placeholder ?? 'Type an entity type and press Enter'}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
    </div>
  );
}
